import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle2, Clock, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import CountrySelect from '@/components/common/CountrySelect';
import type { BillingDetails } from '@/types/billing';

export const billingSchema = z.object({
  legal_company_name: z.string().min(1, 'The name that goes on the invoice'),
  tax_id: z.string().optional(),
  registration_number: z.string().optional(),
  billing_address_line1: z.string().min(1, 'Street address'),
  billing_address_line2: z.string().optional(),
  billing_city: z.string().min(1, 'City'),
  billing_state: z.string().optional(),
  billing_postal_code: z.string().min(1, 'Postal code'),
  billing_country: z.string().min(1, 'Country'),
  billing_contact_name: z.string().min(1, 'Who we should address it to'),
  billing_contact_email: z.string().email('Enter a valid email address'),
  billing_contact_phone: z
    .string()
    .refine((v) => (v.match(/\d/g) ?? []).length >= 6, {
      message: 'Enter a number your advisor can actually call',
    }),
  currency: z.string().optional(),
  invoice_terms_accepted: z.boolean().refine((v) => v, {
    message: 'Agree to pay the monthly invoices to continue',
  }),
});

export type BillingValues = z.infer<typeof billingSchema>;

const EMPTY: BillingValues = {
  legal_company_name: '',
  tax_id: '',
  registration_number: '',
  billing_address_line1: '',
  billing_address_line2: '',
  billing_city: '',
  billing_state: '',
  billing_postal_code: '',
  billing_country: '',
  billing_contact_name: '',
  billing_contact_email: '',
  billing_contact_phone: '',
  currency: '',
  invoice_terms_accepted: false,
};

interface Props {
  initial?: BillingDetails | null;
  onSubmit: (values: BillingValues) => Promise<void> | void;
  submitting?: boolean;
  submitLabel?: string;
  secondaryAction?: React.ReactNode;
  onBehalfOf?: string;
}

function InvoicingStatusLine({ initial, onBehalfOf }: { initial?: BillingDetails | null; onBehalfOf?: string }) {
  if (!initial?.exists) return null;
  const when = (value?: string | null) => (value ? new Date(value).toLocaleDateString() : '');
  switch (initial.invoicing_status) {
    case 'approved':
      return (
        <p className="flex items-center gap-1.5 pt-1 text-xs text-green-700" data-testid="invoicing-status">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Approved for invoice billing on {when(initial.approved_for_invoicing_at)}.
        </p>
      );
    case 'awaiting_approval':
      return (
        <p className="flex items-center gap-1.5 pt-1 text-xs text-amber-700" data-testid="invoicing-status">
          <Clock className="h-3.5 w-3.5" />
          Agreed on {when(initial.invoice_terms_accepted_at)}; BitSwan is approving {onBehalfOf ? 'them' : 'you'} for invoice billing and will email {onBehalfOf ? 'them' : 'you'}.
        </p>
      );
    default:
      return null;
  }
}

function ViesStatus({ initial, typed }: { initial?: BillingDetails | null; typed?: string }) {
  if (!initial?.exists || !initial.tax_id || (typed ?? '') !== initial.tax_id) {
    return (
      <p className="text-xs text-muted-foreground">
        EU businesses outside Czechia with a VAT ID that VIES confirms are invoiced without VAT under the reverse charge.
      </p>
    );
  }
  const checked = initial.tax_id_vies_checked_at
    ? new Date(initial.tax_id_vies_checked_at).toLocaleDateString()
    : '';
  switch (initial.tax_id_vies_status) {
    case 'valid':
      return (
        <p className="text-xs text-green-700" data-testid="billing-vies-status">
          Confirmed in VIES{initial.tax_id_vies_name ? ` as ${initial.tax_id_vies_name}` : ''}{checked ? ` on ${checked}` : ''}.
        </p>
      );
    case 'invalid':
      return (
        <p className="text-xs text-amber-700" data-testid="billing-vies-status">
          VIES does not know this VAT ID. Check it: an EU business outside Czechia needs a VIES-valid one to be invoiced without VAT.
        </p>
      );
    case 'unavailable':
      return (
        <p className="text-xs text-muted-foreground" data-testid="billing-vies-status">
          VIES could not be reached; it is checked again before each invoice.
        </p>
      );
    case 'not_eu':
      return (
        <p className="text-xs text-muted-foreground" data-testid="billing-vies-status">
          Not an EU VAT ID, so VIES is not consulted.
        </p>
      );
    default:
      return null;
  }
}

export default function BillingDetailsForm({
  initial,
  onSubmit,
  submitting,
  submitLabel = 'Save billing details',
  secondaryAction,
  onBehalfOf,
}: Props) {
  const form = useForm<BillingValues>({
    resolver: zodResolver(billingSchema),
    defaultValues: EMPTY,
  });

  React.useEffect(() => {
    if (!initial?.exists) return;
    form.reset({
      ...EMPTY,
      ...Object.fromEntries(
        Object.keys(EMPTY)
          .filter((key) => key !== 'invoice_terms_accepted')
          .map((key) => [key, (initial as Record<string, unknown>)[key] ?? '']),
      ),
      invoice_terms_accepted: Boolean(initial.invoice_terms_accepted_at),
    } as BillingValues);
  }, [initial, form]);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4"
        data-testid="billing-form"
      >
        <FormField
          control={form.control}
          name="legal_company_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Legal company name</FormLabel>
              <FormControl>
                <Input {...field} data-testid="billing-legal-name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="tax_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>VAT ID (DIČ)</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="CZ12345678" data-testid="billing-tax-id" />
                </FormControl>
                <ViesStatus initial={initial} typed={field.value} />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="registration_number"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Registration number</FormLabel>
                <FormControl>
                  <Input {...field} data-testid="billing-registration-number" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="billing_address_line1"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Address</FormLabel>
              <FormControl>
                <Input {...field} data-testid="billing-address-1" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="billing_address_line2"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Address line 2</FormLabel>
              <FormControl>
                <Input {...field} data-testid="billing-address-2" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid gap-3 sm:grid-cols-3">
          <FormField
            control={form.control}
            name="billing_city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>City</FormLabel>
                <FormControl>
                  <Input {...field} data-testid="billing-city" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="billing_postal_code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Postal code</FormLabel>
                <FormControl>
                  <Input {...field} data-testid="billing-postal-code" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="billing_country"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Country</FormLabel>
                <FormControl>
                  <CountrySelect value={field.value} onChange={field.onChange} testId="billing-country" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <FormField
            control={form.control}
            name="billing_contact_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Billing contact</FormLabel>
                <FormControl>
                  <Input {...field} data-testid="billing-contact-name" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="billing_contact_email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Billing email</FormLabel>
                <FormControl>
                  <Input type="email" {...field} data-testid="billing-contact-email" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="billing_contact_phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone</FormLabel>
                <FormControl>
                  <Input {...field} data-testid="billing-contact-phone" />
                </FormControl>
                <FormDescription>
                  How your advisor reaches you before the trial runs out.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="invoice_terms_accepted"
          render={({ field }) => (
            <FormItem className="rounded-lg border bg-muted/30 p-4">
              <div className="flex items-start gap-3">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={(checked) => field.onChange(checked === true)}
                    data-testid="billing-agree-invoices"
                  />
                </FormControl>
                <div className="space-y-1 leading-tight">
                  <FormLabel className="font-medium">
                    {onBehalfOf ? `${onBehalfOf} agrees to pay the monthly invoices` : 'We agree to pay the monthly invoices'}
                  </FormLabel>
                  <FormDescription>
                    Once BitSwan approves invoice billing, servers, {onBehalfOf ? 'your' : "your advisor's"} package and any hours are
                    invoiced monthly to the billing email above, payable by the due date on each invoice.{onBehalfOf ? ` Tick this only if ${onBehalfOf} has agreed to that.` : ''}
                    Invoices still open after the due date are re-sent every day until they are paid.
                  </FormDescription>
                  <InvoicingStatusLine initial={initial} onBehalfOf={onBehalfOf} />
                </div>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex items-center gap-2 pt-2">
          <Button type="submit" disabled={submitting} data-testid="billing-submit">
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {submitLabel}
          </Button>
          {secondaryAction}
        </div>
      </form>
    </Form>
  );
}
